import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Button, Empty, Spin, Table, Tabs, Typography } from 'antd'
import { DownloadOutlined } from '@ant-design/icons'
import { adminApi } from '../api'
import type { KnowledgeScope } from '../constants/knowledgeScope'
import {
  type FileRec,
  type PreviewKind,
  fetchAttachmentBlob,
  fetchAttachmentPreviewDocx,
  fetchAttachmentPreviewText,
  fetchSpreadsheetPreviewPage,
  listPreviewableFiles,
  pickPrimaryPreviewFileIndex,
  renderDocxHtml,
  resolvePreviewKind,
  resolvePreviewLoadError,
} from './knowledgeFilePreview'
import { ChatMarkdown } from './ChatMarkdown'
import './KnowledgeFilePreviewDrawer.css'

type ScopeParams = { scope: KnowledgeScope }

const EXCEL_PAGE_SIZE = 200
/** 搜索预览新标签页：顶栏 + Tab + padding 占用高度 */
const PAGE_PREVIEW_HEIGHT = 'calc(100vh - 156px)'

type ItemMeta = {
  id: number
  title: string
  files_json?: FileRec[] | null
}

export type KnowledgeFilePreviewPanelProps = {
  itemId: number | null
  scopeParams: ScopeParams
  /** false 时不加载（如 Drawer 关闭） */
  active?: boolean
  /** drawer=抽屉；page=搜索新标签页 */
  layout?: 'drawer' | 'page'
  bodyClassName?: string
  onTitleChange?: (title: string) => void
  /** PDF 预览跳转页码（浏览器内置 PDF 查看器 #page=N） */
  pdfPage?: number | null
  /** PDF 内搜索高亮（浏览器内置查看器 #search=） */
  pdfSearch?: string | null
}

export async function downloadKnowledgeAttachment(
  itemId: number,
  rec: FileRec,
  scopeParams: ScopeParams,
) {
  const { url, blob } = await fetchAttachmentBlob(itemId, rec, scopeParams)
  const a = document.createElement('a')
  a.href = url
  a.download = rec.original_name || rec.stored_name || 'download'
  a.click()
  URL.revokeObjectURL(url)
  void blob
}

export function KnowledgeFilePreviewPanel({
  itemId,
  scopeParams,
  active = true,
  layout = 'drawer',
  bodyClassName,
  onTitleChange,
  pdfPage = null,
  pdfSearch = null,
}: KnowledgeFilePreviewPanelProps) {
  const [metaLoading, setMetaLoading] = useState(false)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [item, setItem] = useState<ItemMeta | null>(null)
  const [fileIdx, setFileIdx] = useState(0)
  const [kind, setKind] = useState<PreviewKind>('unsupported')
  const [blobUrl, setBlobUrl] = useState<string | null>(null)
  const [docxHtml, setDocxHtml] = useState('')
  const [plainText, setPlainText] = useState('')
  const [excelHeader, setExcelHeader] = useState<string[]>([])
  const [excelDataRows, setExcelDataRows] = useState<string[][]>([])
  const [excelSheetNames, setExcelSheetNames] = useState<string[]>([])
  const [excelSheetIndex, setExcelSheetIndex] = useState(0)
  const [excelPage, setExcelPage] = useState(1)
  const [excelTotal, setExcelTotal] = useState(0)
  const [excelTruncated, setExcelTruncated] = useState(false)
  const [excelMaxRows, setExcelMaxRows] = useState(5000)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [previewError, setPreviewError] = useState<string | null>(null)
  const blobUrlRef = useRef<string | null>(null)
  const files = useMemo(() => listPreviewableFiles(item?.files_json), [item])
  const activeFile = files[fileIdx]
  const previewTitle =
    activeFile?.original_name || activeFile?.stored_name || item?.title || '文件'

  const resetExcelPreview = useCallback(() => {
    setExcelHeader([])
    setExcelDataRows([])
    setExcelSheetNames([])
    setExcelSheetIndex(0)
    setExcelPage(1)
    setExcelTotal(0)
    setExcelTruncated(false)
    setExcelMaxRows(5000)
  }, [])

  const replaceBlobUrl = useCallback((next: string | null) => {
    if (blobUrlRef.current && blobUrlRef.current !== next) {
      URL.revokeObjectURL(blobUrlRef.current)
    }
    blobUrlRef.current = next
    setBlobUrl(next)
  }, [])

  useEffect(() => {
    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current)
        blobUrlRef.current = null
      }
    }
  }, [])

  const loadMeta = useCallback(() => {
    if (!itemId || !active) return
    setMetaLoading(true)
    setLoadError(null)
    adminApi
      .get<ItemMeta>(`/admin/knowledge/items/${itemId}`, { params: scopeParams })
      .then((r) => {
        const list = listPreviewableFiles(r.data.files_json)
        setItem(r.data)
        setFileIdx(pickPrimaryPreviewFileIndex(list))
        setLoadError(null)
        onTitleChange?.(r.data.title || previewTitle)
      })
      .catch((e) => {
        setLoadError(resolvePreviewLoadError(e, 'item'))
        setItem(null)
      })
      .finally(() => setMetaLoading(false))
  }, [itemId, scopeParams, active, onTitleChange, previewTitle])

  useEffect(() => {
    if (active && itemId) loadMeta()
    if (!active) {
      setItem(null)
      setFileIdx(0)
      replaceBlobUrl(null)
      setDocxHtml('')
      setPlainText('')
      resetExcelPreview()
      setLoadError(null)
      setPreviewError(null)
    }
  }, [active, itemId, loadMeta, resetExcelPreview, replaceBlobUrl])

  useEffect(() => {
    if (!active || !itemId || !activeFile) return

    let cancelled = false
    const k = resolvePreviewKind(activeFile)
    setKind(k)
    setPreviewLoading(true)
    setPreviewError(null)
    replaceBlobUrl(null)
    setDocxHtml('')
    setPlainText('')
    resetExcelPreview()

    if (k === 'unsupported') {
      setPreviewLoading(false)
      return
    }

    if (k === 'xlsx') {
      setPreviewLoading(false)
      return
    }

    const load = async () => {
      if (k === 'doc' || k === 'docx') {
        const blob =
          k === 'docx'
            ? (await fetchAttachmentBlob(itemId, activeFile, scopeParams)).blob
            : await fetchAttachmentPreviewDocx(itemId, activeFile, scopeParams)
        if (!cancelled) setDocxHtml(await renderDocxHtml(blob))
        return
      }
      if (k === 'pptx' || k === 'markdown') {
        if (!cancelled) {
          setPlainText(await fetchAttachmentPreviewText(itemId, activeFile, scopeParams))
        }
        return
      }
      if (k === 'html') {
        const { blob } = await fetchAttachmentBlob(itemId, activeFile, scopeParams)
        if (!cancelled) setDocxHtml(await blob.text())
        return
      }
      const { url } = await fetchAttachmentBlob(itemId, activeFile, scopeParams)
      if (cancelled) {
        URL.revokeObjectURL(url)
        return
      }
      if (k === 'pdf' || k === 'image') {
        replaceBlobUrl(url)
        return
      }
      URL.revokeObjectURL(url)
    }

    void load()
      .catch((e) => {
        if (!cancelled) setPreviewError(resolvePreviewLoadError(e, 'file'))
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [active, itemId, activeFile, fileIdx, scopeParams, resetExcelPreview, replaceBlobUrl])

  useEffect(() => {
    if (!active || !itemId || !activeFile || kind !== 'xlsx') return

    const offset = (excelPage - 1) * EXCEL_PAGE_SIZE
    setPreviewLoading(true)
    void fetchSpreadsheetPreviewPage(itemId, activeFile, scopeParams, {
      sheet_index: excelSheetIndex,
      offset,
      limit: EXCEL_PAGE_SIZE,
    })
      .then((data) => {
        setExcelSheetNames(data.sheet_names || [])
        setExcelTotal(data.total_data_rows || 0)
        setExcelTruncated(Boolean(data.truncated_by_config))
        setExcelMaxRows(data.max_rows_per_sheet || 5000)
        if (data.header?.length) {
          setExcelHeader(data.header)
        }
        setExcelDataRows(data.rows || [])
      })
      .catch((e) => setPreviewError(resolvePreviewLoadError(e, 'file')))
      .finally(() => setPreviewLoading(false))
  }, [active, itemId, activeFile, kind, fileIdx, excelSheetIndex, excelPage, scopeParams])

  const excelColumns = useMemo(() => {
    const cols = excelHeader.length || excelDataRows[0]?.length || 1
    return Array.from({ length: cols }, (_, i) => ({
      title: String(excelHeader[i] ?? `列${i + 1}`),
      dataIndex: String(i),
      key: i,
      ellipsis: true,
      width: 120,
    }))
  }, [excelHeader, excelDataRows])

  const excelData = useMemo(() => {
    const base = (excelPage - 1) * EXCEL_PAGE_SIZE
    return excelDataRows.map((row, i) => {
      const rec: Record<string, string> = { key: String(base + i) }
      row.forEach((cell, j) => {
        rec[String(j)] = cell
      })
      return rec
    })
  }, [excelDataRows, excelPage])

  const pdfSrc = useMemo(() => {
    if (!blobUrl) return null
    const hash: string[] = []
    if (pdfPage != null && pdfPage > 0) {
      hash.push(`page=${pdfPage}`)
    }
    const term = (pdfSearch || '').trim()
    if (term) {
      hash.push(`search=${encodeURIComponent(term)}`)
    }
    if (!hash.length) return blobUrl
    return `${blobUrl}#${hash.join('&')}`
  }, [blobUrl, pdfPage, pdfSearch])

  const previewBody = () => {
    if (loadError) {
      return <Empty description={loadError} />
    }
    if (!files.length) {
      return <Empty description="该条目无上传文件，无法预览" />
    }
    if (previewError) {
      return <Empty description={previewError} />
    }
    if (previewLoading) {
      return (
        <div className="kb-file-preview-loading">
          <Spin description="加载预览…" />
        </div>
      )
    }
    if (kind === 'pdf' && pdfSrc) {
      const frameHeight = isPageLayout ? PAGE_PREVIEW_HEIGHT : '100%'
      return (
        <div
          className="kb-file-preview-pdf-wrap"
          style={isPageLayout ? { height: PAGE_PREVIEW_HEIGHT } : undefined}
        >
          <iframe
            key={`${blobUrl}-${pdfPage ?? 0}-${pdfSearch ?? ''}`}
            title="pdf-preview"
            className="kb-file-preview-frame"
            src={pdfSrc}
            style={{ width: '100%', height: frameHeight, border: 0, display: 'block' }}
          />
        </div>
      )
    }
    if (kind === 'image' && blobUrl) {
      return (
        <div className="kb-file-preview-image-wrap">
          <img src={blobUrl} alt={activeFile?.original_name || 'preview'} className="kb-file-preview-image" />
        </div>
      )
    }
    if ((kind === 'docx' || kind === 'doc' || kind === 'html') && docxHtml) {
      return <div className="kb-file-preview-docx" dangerouslySetInnerHTML={{ __html: docxHtml }} />
    }
    if (kind === 'markdown' && plainText) {
      return (
        <div className="kb-file-preview-markdown">
          <ChatMarkdown content={plainText} />
        </div>
      )
    }
    if (kind === 'pptx' && plainText) {
      return (
        <div className="kb-file-preview-plain">
          <pre className="kb-file-preview-plain-pre">{plainText}</pre>
        </div>
      )
    }
    if (kind === 'xlsx') {
      if (!excelHeader.length && !excelDataRows.length) {
        return <Empty description="表格无数据或无法读取" />
      }
      return (
        <>
          {excelTruncated ? (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 8 }}
              message={`在线预览最多展示每表 ${excelMaxRows} 行（含表头），与「切片管理」中行数上限一致；更多内容请下载查看。`}
            />
          ) : null}
          {excelSheetNames.length > 1 ? (
            <Tabs
              activeKey={String(excelSheetIndex)}
              onChange={(k) => {
                setExcelSheetIndex(Number(k))
                setExcelPage(1)
              }}
              size="small"
              items={excelSheetNames.map((name, i) => ({
                key: String(i),
                label: name || `工作表${i + 1}`,
              }))}
              style={{ marginBottom: 8 }}
            />
          ) : excelSheetNames[0] ? (
            <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
              工作表：{excelSheetNames[excelSheetIndex] || excelSheetNames[0]}
            </Typography.Text>
          ) : null}
          <Table
            className="kb-file-preview-xlsx"
            size="small"
            scroll={{ x: 'max-content', y: 'calc(100vh - 280px)' }}
            pagination={{
              current: excelPage,
              pageSize: EXCEL_PAGE_SIZE,
              total: excelTotal,
              showSizeChanger: false,
              showTotal: (t) => `约 ${t} 行数据`,
              onChange: (p) => setExcelPage(p),
            }}
            columns={excelColumns}
            dataSource={excelData}
          />
        </>
      )
    }
    if (kind === 'unsupported') {
      return (
        <Empty
          description="暂不支持在线预览此格式，请下载后查看"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        >
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={() => activeFile && void downloadKnowledgeAttachment(itemId!, activeFile, scopeParams)}
          >
            下载文件
          </Button>
        </Empty>
      )
    }
    return <Empty description="预览加载失败，请稍后重试" />
  }

  const isPageLayout = layout === 'page'
  const resolvedBodyClass =
    bodyClassName ??
    (isPageLayout ? 'kb-file-preview-body kb-file-preview-body--page' : 'kb-file-preview-body')

  const fileTabs =
    files.length > 1 ? (
      <Tabs
        activeKey={String(fileIdx)}
        onChange={(k) => setFileIdx(Number(k))}
        items={files.map((f, i) => ({
          key: String(i),
          label: f.original_name || f.stored_name || `文件${i + 1}`,
        }))}
        style={{ marginBottom: 8, flexShrink: 0 }}
      />
    ) : null

  const bodyEl = (
    <div
      className={resolvedBodyClass}
      style={isPageLayout ? { flex: 1, minHeight: 0, overflow: 'auto' } : undefined}
    >
      {previewBody()}
    </div>
  )

  if (isPageLayout) {
    return (
      <div className="kb-file-preview-panel-root--page">
        {metaLoading ? (
          <div className="kb-file-preview-loading" style={{ height: PAGE_PREVIEW_HEIGHT }}>
            <Spin description="加载预览…" />
          </div>
        ) : (
          <>
            {fileTabs}
            {bodyEl}
          </>
        )}
      </div>
    )
  }

  return (
    <div>
      <Spin spinning={metaLoading}>
        {fileTabs}
        {bodyEl}
      </Spin>
    </div>
  )
}

export function useKnowledgePreviewActiveFile(
  itemId: number | null,
  scopeParams: ScopeParams,
  active = true,
) {
  const [item, setItem] = useState<ItemMeta | null>(null)
  const [fileIdx, setFileIdx] = useState(0)

  useEffect(() => {
    if (!active || !itemId) {
      setItem(null)
      setFileIdx(0)
      return
    }
    adminApi
      .get<ItemMeta>(`/admin/knowledge/items/${itemId}`, { params: scopeParams })
      .then((r) => {
        const list = listPreviewableFiles(r.data.files_json)
        setItem(r.data)
        setFileIdx(pickPrimaryPreviewFileIndex(list))
      })
      .catch(() => setItem(null))
  }, [active, itemId, scopeParams])

  const files = useMemo(() => listPreviewableFiles(item?.files_json), [item])

  return { item, files, fileIdx, setFileIdx, activeFile: files[fileIdx] }
}
